import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, field, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { Category } from './Category.js';
import { Product } from './Product.js';
import { WebshopMetaData, WebshopPrivateMetaData, WebshopStatus } from './WebshopMetaData.js';
export class WebshopPreview extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.organizationId = '';
        /**
         * Not writeable
         */
        this.uri = '';
        /**
         * Not writeable
         */
        this.legacyUri = null;
        this.domain = null;
        this.domainUri = null;
        this.meta = WebshopMetaData.create({});
        this.privateMeta = WebshopPrivateMetaData.create({});
        this.createdAt = new Date();
    }
    get hasSingleTickets() {
        return this.meta.hasSingleTickets;
    }
    get hasTickets() {
        return this.meta.hasTickets;
    }
    getDefaultDomain(organization) {
        var _a;
        if (!STAMHOOFD.domains.webshop) {
            console.error('No webshop domains configured');
            return '';
        }
        return ((_a = STAMHOOFD.domains.webshop[organization.address.country]) !== null && _a !== void 0 ? _a : STAMHOOFD.domains.webshop['']);
    }
    getDefaultUrl(organization) {
        return this.getDefaultDomain(organization) + this.getDefaultSuffix();
    }
    getDomainUrl() {
        return this.domain + this.getDomainSuffix();
    }
    getUrl(organization) {
        if (this.domain && this.meta.domainActive) {
            return this.getDomainUrl();
        }
        return this.getDefaultUrl(organization);
    }
    getLegacyUrl(organization) {
        if (!STAMHOOFD.domains.legacyWebshop || this.legacyUri === null) {
            return null;
        }
        return organization.uri + '.' + STAMHOOFD.domains.legacyWebshop + (this.legacyUri ? '/' + this.legacyUri : '');
    }
    getDomainSuffix() {
        if (!this.domainUri) {
            return '';
        }
        return '/' + this.domainUri;
    }
    getDefaultSuffix() {
        if (!this.uri) {
            return '';
        }
        return '/' + this.uri;
    }
    getUrlSuffix() {
        if (this.domain) {
            return this.getDomainSuffix();
        }
        return this.getDefaultSuffix();
    }
    isClosed(margin = 0) {
        if (this.meta.status !== WebshopStatus.Open || (this.meta.availableUntil && this.meta.availableUntil.getTime() < new Date().getTime() + margin)) {
            return true;
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], WebshopPreview.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 378 })
], WebshopPreview.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 134 })
], WebshopPreview.prototype, "uri", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 89, field: 'uri' }),
    field({
        decoder: StringDecoder,
        nullable: true,
        version: 134,
        downgrade: function () {
            var _a;
            return (_a = this.legacyUri) !== null && _a !== void 0 ? _a : this.uri;
        },
    })
], WebshopPreview.prototype, "legacyUri", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 89 })
], WebshopPreview.prototype, "domain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 89 })
], WebshopPreview.prototype, "domainUri", void 0);
__decorate([
    field({ decoder: WebshopMetaData })
], WebshopPreview.prototype, "meta", void 0);
__decorate([
    field({ decoder: WebshopPrivateMetaData, version: 62 })
], WebshopPreview.prototype, "privateMeta", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 366 })
], WebshopPreview.prototype, "createdAt", void 0);
export class Webshop extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.organizationId = '';
        /**
         * Not writeable
         */
        this.uri = '';
        /**
         * Not writeable
         */
        this.legacyUri = null;
        this.domain = null;
        this.domainUri = null;
        this.meta = WebshopMetaData.create({});
        this.products = [];
        this.categories = [];
    }
    get hasSingleTickets() {
        return this.meta.hasSingleTickets;
    }
    get hasTickets() {
        return this.meta.hasTickets;
    }
    get productsInOrder() {
        if (this.categories.length > 0) {
            return this.categories.flatMap(category => category.productIds.flatMap((id) => {
                const product = this.products.find(product => product.id === id);
                if (product) {
                    return [product];
                }
                return [];
            }));
        }
        return this.products;
    }
    /**
     * Whether we need to show the text 'Free' in webshops (only if we have at least one non-free product)
     */
    get isAllFree() {
        for (const product of this.products) {
            for (const price of product.prices) {
                if (price.price) {
                    return false;
                }
            }
            for (const menu of product.optionMenus) {
                for (const option of menu.options) {
                    if (option.price) {
                        return false;
                    }
                }
            }
        }
        for (const plan of this.meta.seatingPlans) {
            for (const category of plan.categories) {
                if (category.price) {
                    return false;
                }
            }
        }
        if (this.meta.paymentConfiguration.administrationFee.fixed) {
            return false;
        }
        return true;
    }
    get canEnableCart() {
        if (this.products.length === 1 && !this.meta.allowDiscountCodeEntry) {
            const product = this.products[0];
            if (product.isUnique) {
                return false;
            }
        }
        return true;
    }
    get shouldEnableCart() {
        if (!this.meta.cartEnabled) {
            return false;
        }
        return this.canEnableCart;
    }
    get hasCustomDomain() {
        return !!this.domain && this.meta.domainActive;
    }
    getDefaultDomain(organization) {
        var _a;
        if (!STAMHOOFD.domains.webshop) {
            console.error('No webshop domains configured');
            return '';
        }
        return ((_a = STAMHOOFD.domains.webshop[organization.address.country]) !== null && _a !== void 0 ? _a : STAMHOOFD.domains.webshop['']);
    }
    getDefaultUrl(organization) {
        return this.getDefaultDomain(organization) + this.getDefaultSuffix();
    }
    getDomainUrl() {
        return this.domain + this.getDomainSuffix();
    }
    getUrl(organization) {
        if (this.hasCustomDomain) {
            return this.getDomainUrl();
        }
        return this.getDefaultUrl(organization);
    }
    getLegacyUrl(organization) {
        if (!STAMHOOFD.domains.legacyWebshop || this.legacyUri === null) {
            return null;
        }
        return organization.uri + '.' + STAMHOOFD.domains.legacyWebshop + (this.legacyUri ? '/' + this.legacyUri : '');
    }
    getDomainSuffix() {
        if (!this.domainUri) {
            return '';
        }
        return '/' + this.domainUri;
    }
    getDefaultSuffix() {
        if (!this.uri) {
            return '';
        }
        return '/' + this.uri;
    }
    getUrlSuffix() {
        if (this.domain) {
            return this.getDomainSuffix();
        }
        return this.getDefaultSuffix();
    }
    buildDNSRecords() {
        if (!this.domain) {
            return [];
        }
        return WebshopPrivateMetaData.buildDNSRecords(this.domain);
    }
    isClosed(margin = 0) {
        if (this.meta.status !== WebshopStatus.Open || (this.meta.availableUntil && this.meta.availableUntil.getTime() < new Date().getTime() + margin) || this.opensInTheFuture()) {
            return true;
        }
        return false;
    }
    opensInTheFuture() {
        if (this.meta.status !== WebshopStatus.Open) {
            return false;
        }
        if (this.meta.openAt && this.meta.openAt.getTime() > new Date().getTime()) {
            return true;
        }
        return false;
    }
    clearStock() {
        for (const product of this.products) {
            product.clearStock();
        }
        for (const method of this.meta.checkoutMethods) {
            method.clearStock();
        }
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Webshop.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 378 })
], Webshop.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 134 })
], Webshop.prototype, "uri", void 0);
__decorate([
    field({ decoder: StringDecoder, field: 'uri' }),
    field({
        decoder: StringDecoder,
        nullable: true,
        version: 134,
        downgrade: function () {
            var _a;
            return (_a = this.legacyUri) !== null && _a !== void 0 ? _a : this.uri;
        },
    })
], Webshop.prototype, "legacyUri", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Webshop.prototype, "domain", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Webshop.prototype, "domainUri", void 0);
__decorate([
    field({ decoder: WebshopMetaData })
], Webshop.prototype, "meta", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Product) })
], Webshop.prototype, "products", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Category) })
], Webshop.prototype, "categories", void 0);
export class PrivateWebshop extends Webshop {
    constructor() {
        super(...arguments);
        this.privateMeta = WebshopPrivateMetaData.create({});
    }
}
__decorate([
    field({ decoder: WebshopPrivateMetaData })
], PrivateWebshop.prototype, "privateMeta", void 0);
//# sourceMappingURL=Webshop.js.map